// ViewFieldJSONDrawer.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ViewFieldJSONDrawer.css";

const safeStringify = (obj) => {
  try {
    return JSON.stringify(obj ?? {}, null, 2);
  } catch {
    return "{\n  \n}";
  }
};

const tryParseJson = (text) => {
  try {
    return { ok: true, value: JSON.parse(text), error: "" };
  } catch (e) {
    return { ok: false, value: null, error: e?.message || String(e) };
  }
};

export default function ViewFieldJSONDrawer({
  title = "Field JSON",
  field = null,
  onClose,
  onApply,
  width = 760,
}) {
  const textareaRef = useRef(null);

  const seedText = useMemo(() => safeStringify(field || {}), [field]);
  const [text, setText] = useState(seedText);

  useEffect(() => {
    setText(seedText);
  }, [seedText]);

  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus?.(), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const parse = useMemo(() => tryParseJson(text), [text]);

  const hasChanges = useMemo(() => {
    const a = tryParseJson(seedText);
    const b = tryParseJson(text);
    if (!a.ok || !b.ok) return true;
    return JSON.stringify(a.value) !== JSON.stringify(b.value);
  }, [seedText, text]);

  const handleApply = () => {
    if (!parse.ok) return;
    const next = parse.value;
    if (!next || typeof next !== "object" || Array.isArray(next)) return;
    onApply?.(next);
    onClose?.();
  };

  return (
    <div className="vmfd-sidepanel" style={{ width }}>
      <div className="vmfd-sidepanel-header">
        <div className="vmfd-sidepanel-title">
          {title}
          {field?.field ? (
            <span className="vmfd-sidepanel-subtitle">{String(field.field)}</span>
          ) : null}
        </div>

        <button
          type="button"
          className="vmfd-sidepanel-close"
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          ×
        </button>
      </div>

      <div className="vmfd-sidepanel-body">
        <label className="vmfd-label">Edit JSON</label>

        <textarea
          ref={textareaRef}
          className="vmfd-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
        />

        {!parse.ok ? (
          <div className="vmfd-error" role="alert">
            JSON error: {parse.error}
          </div>
        ) : null}
      </div>

      <div className="vmfd-sidepanel-footer">
        <div className="vmfd-status">
          {!parse.ok ? "Fix JSON errors" : hasChanges ? "Unsaved changes" : "No changes"}
        </div>

        <div className="vmfd-actions">
          <button type="button" className="vmfd-btn vmfd-btn--ghost" onClick={onClose}>
            Cancel
          </button>

          <button
            type="button"
            className="vmfd-btn vmfd-btn--primary"
            onClick={handleApply}
            disabled={!parse.ok || !hasChanges}
            title={!parse.ok ? "Fix JSON errors first" : !hasChanges ? "No changes" : "Apply changes"}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
